import React, { useEffect, useRef, useState } from 'react';
import { useStore } from '../context/StoreContext.tsx';
import { GoogleGenAI, LiveServerMessage, Modality } from '@google/genai';
import { Mic, MicOff, PhoneOff, Volume2, Loader2, AlertCircle, Key } from 'lucide-react';
import { CHIP_LOGO } from '../constants.ts';

// --- Audio Helpers (PCM Encoding/Decoding) ---
const floatTo16BitPCM = (input: Float32Array) => {
    const output = new Int16Array(input.length);
    for (let i = 0; i < input.length; i++) {
        const s = Math.max(-1, Math.min(1, input[i]));
        output[i] = s < 0 ? s * 0x8000 : s * 0x7FFF;
    }
    return output;
};

const base64ToUint8Array = (base64: string) => {
    const binaryString = atob(base64);
    const bytes = new Uint8Array(binaryString.length);
    for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
    }
    return bytes;
};

const arrayBufferToBase64 = (buffer: ArrayBuffer) => {
    let binary = '';
    const bytes = new Uint8Array(buffer);
    const len = bytes.byteLength;
    for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
};

const TalkToChip: React.FC = () => {
  const { dna } = useStore();
  const [isConnected, setIsConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicOn, setIsMicOn] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [volumeLevel, setVolumeLevel] = useState(0); // For visualizer
  const [chipSpeaking, setChipSpeaking] = useState(false);

  // Audio Context Refs
  const audioContextRef = useRef<AudioContext | null>(null);
  const mediaStreamRef = useRef<MediaStream | null>(null);
  const inputSourceRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const processorRef = useRef<ScriptProcessorNode | null>(null);
  const nextStartTimeRef = useRef<number>(0);
  
  // Connection Refs
  const sessionRef = useRef<any>(null); // Holds the LiveSession

  const cleanupAudio = () => {
    if (processorRef.current) {
      processorRef.current.disconnect();
      processorRef.current = null;
    }
    if (inputSourceRef.current) {
      inputSourceRef.current.disconnect();
      inputSourceRef.current = null;
    }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(track => track.stop());
      mediaStreamRef.current = null;
    }
    if (audioContextRef.current) {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }
    sessionRef.current = null;
  };

  const handleDisconnect = () => {
    cleanupAudio();
    setIsConnected(false);
    setIsConnecting(false);
    setChipSpeaking(false);
  };

  const startSession = async () => {
    setError(null);
    setIsConnecting(true);

    try {
      const apiKey = process.env.API_KEY || '';
      if (!apiKey) throw new Error("API Key Missing");

      const ai = new GoogleGenAI({ apiKey });
      
      // Initialize Audio Contexts
      const audioCtx = new (window.AudioContext || (window as any).webkitAudioContext)({ sampleRate: 24000 });
      audioContextRef.current = audioCtx;
      
      // Capture User Audio
      const stream = await navigator.mediaDevices.getUserMedia({ audio: {
        channelCount: 1,
        sampleRate: 16000,
      }});
      mediaStreamRef.current = stream;

      // Construct System Prompt from DNA
      const sysInstruction = `
        You are Chip, the mascot for Steelhead Productions. 
        Identity: ${dna.core.archetype}.
        Mission: ${dna.core.mission}.
        Tone: ${dna.voice.tone.join(', ')}.
        Personality: ${dna.personality.traits.join(', ')}.
        Backstory: ${dna.core.backstory}.
        Voice Style: ${dna.voice.styleGuide}.
        
        Rules:
        - Keep responses concise (under 3 sentences usually).
        - Be warm, helpful, and energetic.
        - Do not be a generic AI. Be Chip.
        - If asked about "Exhibit Happy", explain it's about making trade shows fun and stress-free.
      `;

      // Establish Live Connection
      const session = await ai.live.connect({
        model: 'gemini-2.5-flash-native-audio-preview-12-2025',
        config: {
          responseModalities: [Modality.AUDIO],
          systemInstruction: sysInstruction,
          speechConfig: {
            voiceConfig: { prebuiltVoiceConfig: { voiceName: 'Kore' } } // Kore is energetic
          }
        },
        callbacks: {
          onopen: () => {
            setIsConnected(true);
            setIsConnecting(false);
            
            // Setup Input Processing
            const inputCtx = new AudioContext({ sampleRate: 16000 });
            const source = inputCtx.createMediaStreamSource(stream);
            // Using ScriptProcessor (legacy but simple for Live API demo vs AudioWorklet complex setup)
            const processor = inputCtx.createScriptProcessor(4096, 1, 1);
            
            processor.onaudioprocess = (e) => {
              const inputData = e.inputBuffer.getChannelData(0);
              
              // Simple volume meter logic
              let sum = 0;
              for(let i=0; i<inputData.length; i++) sum += inputData[i] * inputData[i];
              const rms = Math.sqrt(sum / inputData.length);
              setVolumeLevel(Math.min(rms * 5, 1)); // Amplify for visualizer

              // Send to Gemini
              const pcmData = floatTo16BitPCM(inputData);
              const base64Audio = arrayBufferToBase64(pcmData.buffer);
              
              session.sendRealtimeInput({
                media: {
                  mimeType: 'audio/pcm;rate=16000',
                  data: base64Audio
                }
              });
            };

            source.connect(processor);
            processor.connect(inputCtx.destination);
            
            inputSourceRef.current = source;
            processorRef.current = processor;
          },
          onmessage: async (msg: LiveServerMessage) => {
            // Handle Output Audio
            const audioData = msg.serverContent?.modelTurn?.parts?.[0]?.inlineData?.data;
            if (audioData) {
              setChipSpeaking(true);
              const audioBytes = base64ToUint8Array(audioData);
              const audioBuffer = await decodeAudioData(audioBytes, audioCtx);
              
              // Schedule playback
              const source = audioCtx.createBufferSource();
              source.buffer = audioBuffer;
              source.connect(audioCtx.destination);
              
              const now = audioCtx.currentTime;
              // Ensure smooth playback without gaps
              const startTime = Math.max(now, nextStartTimeRef.current);
              source.start(startTime);
              nextStartTimeRef.current = startTime + audioBuffer.duration;
              
              source.onended = () => {
                if (audioCtx.currentTime >= nextStartTimeRef.current - 0.1) {
                   setChipSpeaking(false);
                }
              };
            }
            
            if (msg.serverContent?.turnComplete) {
               // Turn finished
            }
          },
          onclose: () => {
            handleDisconnect();
          },
          onerror: (err) => {
            console.error("Live API Error:", err);
            setError("Connection Error");
            handleDisconnect();
          }
        }
      });
      
      sessionRef.current = session;

    } catch (e: any) {
      console.error(e);
      setError(e.message || "Failed to start session");
      setIsConnecting(false);
    }
  };

  // Helper to decode PCM
  const decodeAudioData = async (data: Uint8Array, ctx: AudioContext) => {
    // Convert 16-bit PCM to Float32
    const int16Data = new Int16Array(data.buffer);
    const float32Data = new Float32Array(int16Data.length);
    for (let i = 0; i < int16Data.length; i++) {
        float32Data[i] = int16Data[i] / 32768.0;
    }
    
    const buffer = ctx.createBuffer(1, float32Data.length, 24000);
    buffer.getChannelData(0).set(float32Data);
    return buffer;
  };

  useEffect(() => {
    // Cleanup on unmount
    return () => cleanupAudio();
  }, []);

  const toggleMic = () => {
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getAudioTracks().forEach(track => {
        track.enabled = !isMicOn;
      });
      setIsMicOn(!isMicOn);
    }
  };

  return (
    <div className="max-w-4xl mx-auto h-[calc(100vh-120px)] flex flex-col items-center justify-center relative">
      
      {/* Visualizer Area */}
      <div className="flex-1 w-full flex flex-col items-center justify-center relative">
        
        {/* Connection Status or Visuals */}
        {!isConnected && !isConnecting && !error && (
          <div className="text-center space-y-6 animate-fadeIn">
             <div className="w-32 h-32 rounded-full bg-[var(--bg-secondary)] border-4 border-[var(--border-subtle)] flex items-center justify-center mx-auto shadow-xl">
                <img src={CHIP_LOGO} alt="Chip" className="w-20 h-20 opacity-50 grayscale transition-all duration-500 hover:grayscale-0 hover:opacity-100" />
             </div>
             <div>
               <h2 className="text-3xl font-heading font-bold text-[var(--text-primary)]">Talk to Chip</h2>
               <p className="text-[var(--text-secondary)] mt-2">Real-time voice conversation powered by Gemini Live.</p>
             </div>
             <button 
               onClick={startSession}
               className="btn-primary py-3 px-8 text-lg rounded-full shadow-lg hover:scale-105 transition-transform flex items-center gap-2 mx-auto"
             >
               <Mic size={20} /> Start Conversation
             </button>
          </div>
        )}

        {isConnecting && (
           <div className="text-center animate-pulse">
              <Loader2 size={48} className="animate-spin text-[var(--accent-primary)] mx-auto mb-4" />
              <p className="text-[var(--text-secondary)] font-medium">Connecting to Chip...</p>
           </div>
        )}

        {isConnected && (
           <div className="relative w-full h-full flex flex-col items-center justify-center animate-scaleIn">
              {/* Chip Visualizer Circle */}
              <div 
                className={`relative w-48 h-48 rounded-full flex items-center justify-center transition-all duration-100
                  ${chipSpeaking ? 'shadow-[0_0_50px_var(--accent-primary)] scale-110' : 'shadow-xl'}
                `}
                style={{
                  background: chipSpeaking ? 'var(--accent-primary)' : 'var(--bg-secondary)',
                  border: '4px solid ' + (chipSpeaking ? 'white' : 'var(--border-subtle)')
                }}
              >
                 <img 
                   src={CHIP_LOGO} 
                   alt="Chip" 
                   className="w-32 h-32 object-contain relative z-10" 
                 />
                 
                 {/* Ripple Effects when User Speaks */}
                 {volumeLevel > 0.01 && !chipSpeaking && (
                   <>
                     <div className="absolute inset-0 rounded-full border-2 border-[var(--text-primary)] opacity-20 animate-ping" style={{ animationDuration: `${1 - volumeLevel}s` }}></div>
                     <div className="absolute inset-0 rounded-full border border-[var(--text-primary)] opacity-10 animate-ping" style={{ animationDuration: '1.5s' }}></div>
                   </>
                 )}
              </div>

              <div className="mt-8 text-center h-8">
                 {chipSpeaking ? (
                   <div className="flex items-center gap-1 justify-center">
                      <span className="w-1 h-3 bg-[var(--text-primary)] animate-[bounce_0.5s_infinite_0ms]"></span>
                      <span className="w-1 h-5 bg-[var(--text-primary)] animate-[bounce_0.5s_infinite_100ms]"></span>
                      <span className="w-1 h-3 bg-[var(--text-primary)] animate-[bounce_0.5s_infinite_200ms]"></span>
                      <span className="text-sm font-bold text-[var(--text-primary)] ml-2 uppercase tracking-widest">Chip Speaking</span>
                   </div>
                 ) : (
                   <span className="text-sm text-[var(--text-tertiary)] uppercase tracking-widest font-medium">Listening...</span>
                 )}
              </div>
           </div>
        )}

        {error && (
          <div className="text-center p-6 bg-red-50 border border-red-200 rounded-xl max-w-sm">
             <AlertCircle size={32} className="text-red-500 mx-auto mb-2" />
             <h3 className="font-bold text-red-700">Connection Failed</h3>
             <p className="text-sm text-red-600 mb-4">{error}</p>
             <button onClick={() => setError(null)} className="px-4 py-2 bg-white border border-red-200 rounded-lg text-sm font-medium hover:bg-red-50">Dismiss</button>
          </div>
        )}

      </div>

      {/* Control Bar */}
      {isConnected && (
        <div className="h-24 w-full flex items-center justify-center gap-6 pb-6 animate-slideUp">
           <button 
             onClick={toggleMic}
             className={`p-4 rounded-full shadow-lg transition-all ${isMicOn ? 'bg-[var(--bg-primary)] text-[var(--text-primary)] hover:bg-[var(--bg-tertiary)]' : 'bg-red-100 text-red-600'}`}
           >
             {isMicOn ? <Mic size={24} /> : <MicOff size={24} />}
           </button>

           <button 
             onClick={handleDisconnect}
             className="px-8 py-4 bg-red-500 hover:bg-red-600 text-white rounded-full font-bold shadow-lg flex items-center gap-2 transition-transform hover:scale-105"
           >
             <PhoneOff size={20} /> End Call
           </button>

           <div className="p-4 rounded-full bg-[var(--bg-primary)] text-[var(--text-tertiary)] shadow-sm">
              <Volume2 size={24} />
           </div>
        </div>
      )}

    </div>
  );
};

export default TalkToChip;